import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useRouter } from "next/router";
import AdMock, { useAdsMockEnabled } from "components/ads/AdMock";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export const ADSENSE_CLIENT = "ca-pub-7913582992544944";

/**
 * PC 수평 배너 수락 상한.
 * (full-width-responsive 는 모바일에서 큰 블록을 유발해서 쓰지 않음)
 * 표시 높이는 광고 실제 높이에 맞춤 (상한 이하).
 */
export const AD_HORIZONTAL_HEIGHT = 120;

/** 모바일 수락 상한 — 표시는 실제 높이에 맞춤 */
export const AD_HORIZONTAL_HEIGHT_MOBILE = 160;

/** 허용 높이보다 크면 재요청/미표시. 소수 오차 여유 */
const AD_HEIGHT_TOLERANCE_PX = 2;

/** 사이즈 미달·미채움 시 재요청 횟수 (최초 포함) */
const MAX_AD_ATTEMPTS = 3;

/** layout max-width(800) 기준 — 이하면 모바일 한도 적용 */
const MOBILE_MQ = "(max-width: 800px)";

/** [광고 단위 기준 등록] 노션 슬롯 — 수평 고정 높이 */
export const AD_SLOTS = {
  /** detail 페이지 위쪽 (PriceInfo 아래) */
  detailUpper: "1408950393",
  /** 메인 · 투자매력도 아래 */
  mainBelowAttractiveness: "1879009673",
  /** 메인 최하단 — 밀도 완화로 미사용 (자동광고 여지) */
  mainBottom: "9834235328",
  /** 투자매력도(랭킹) 최하단 */
  rankingBottom: "1667970692",
} as const;

/** /ko · /en 모두 동일 구성으로 광고 노출 */
export const ADS_LOCALES = ["ko", "en"] as const;

type FitState = "pending" | "fit" | "reject";

function useAdsLiveHost() {
  const [live, setLive] = useState(false);
  useEffect(() => {
    const h = window.location.hostname;
    setLive(h === "riskweather.io" || h === "www.riskweather.io");
  }, []);
  return live;
}

/** PC 120 / 모바일 160 — 수락 상한 */
function useAdMaxHeight() {
  const [maxH, setMaxH] = useState(AD_HORIZONTAL_HEIGHT);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const apply = () =>
      setMaxH(mq.matches ? AD_HORIZONTAL_HEIGHT_MOBILE : AD_HORIZONTAL_HEIGHT);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return maxH;
}

/** AdSense가 심은 iframe/ins 의 ‘원래’ 높이 (CSS 강제 전 값) */
function readNaturalAdHeight(root: HTMLElement, attempt: number): number | null {
  const ins = root.querySelector<HTMLElement>(
    `.adsbygoogle[data-rw-attempt="${attempt}"]`
  );
  if (!ins) return null;

  const status = ins.getAttribute("data-ad-status");
  if (status === "unfilled") return -1;

  const iframe = ins.querySelector("iframe") || root.querySelector("iframe");
  if (!iframe) {
    if (status === "filled") return null;
    return null;
  }

  const attrH = Number(iframe.getAttribute("height"));
  if (Number.isFinite(attrH) && attrH > 0) return attrH;

  const styleH = parseInt(String(iframe.style.height || ""), 10);
  if (Number.isFinite(styleH) && styleH > 0) return styleH;

  const boxH = iframe.offsetHeight || iframe.getBoundingClientRect().height;
  if (boxH > 0) return Math.round(boxH);

  return null;
}

function isAcceptableHeight(h: number, maxH: number): boolean {
  if (h <= 0) return false;
  return h <= maxH + AD_HEIGHT_TOLERANCE_PX;
}

type AdSenseResponsiveProps = {
  slot: string;
  className?: string;
};

/**
 * AdSense 수평 단위 — PC 120 / 모바일 160 이하만 수락.
 * 표시 높이는 광고 실제 높이에 맞춤 (고정 슬롯 여백 방지).
 * /ko · /en 동일. 미채움·초과 시 최대 3회 재요청 후 숨김.
 */
function AdSenseResponsive({ slot, className = "" }: AdSenseResponsiveProps) {
  const router = useRouter();
  const mock = useAdsMockEnabled();
  const live = useAdsLiveHost();
  const maxH = useAdMaxHeight();
  const rootRef = useRef<HTMLDivElement>(null);
  const [attempt, setAttempt] = useState(1);
  const [fit, setFit] = useState<FitState>("pending");
  /** 수락된 광고의 실제 높이 — 표시에 사용 */
  const [fittedH, setFittedH] = useState<number | null>(null);
  const localeOk = ADS_LOCALES.includes(
    router.locale as (typeof ADS_LOCALES)[number]
  );

  // attempt마다 새 <ins> 마운트 후 push 1회
  useEffect(() => {
    if (!localeOk || mock || !live) return;
    if (fit !== "pending") return;
    const id = window.requestAnimationFrame(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        // AdSense 스크립트 미로드 시 무시
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [localeOk, mock, live, slot, attempt, fit]);

  // 채움/높이 판정 → 적합하면 실제 높이로 fit, 아니면 재시도 또는 reject
  useEffect(() => {
    if (!localeOk || mock || !live) return;
    if (fit !== "pending") return;
    const root = rootRef.current;
    if (!root) return;

    let settled = false;
    const finish = (ok: boolean, naturalH?: number) => {
      if (settled) return;
      settled = true;
      if (ok && naturalH != null && naturalH > 0) {
        setFittedH(naturalH);
        setFit("fit");
        return;
      }
      if (attempt < MAX_AD_ATTEMPTS) {
        setAttempt((n) => n + 1);
      } else {
        setFit("reject");
      }
    };

    const decide = () => {
      if (settled) return;
      const h = readNaturalAdHeight(root, attempt);
      if (h === null) return;
      if (!isAcceptableHeight(h, maxH)) {
        finish(false);
        return;
      }
      finish(true, h);
    };

    const observer = new MutationObserver(decide);
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-ad-status", "height", "style", "data-rw-attempt"],
    });

    root.addEventListener("load", decide, true);
    decide();

    const timer = window.setTimeout(() => {
      if (settled) return;
      const h = readNaturalAdHeight(root, attempt);
      if (h === null || !isAcceptableHeight(h, maxH)) {
        finish(false);
        return;
      }
      finish(true, h);
    }, 3500);

    return () => {
      observer.disconnect();
      root.removeEventListener("load", decide, true);
      window.clearTimeout(timer);
    };
  }, [localeOk, mock, live, slot, attempt, fit, maxH]);

  if (!localeOk) return null;

  if (mock) {
    return (
      <AdMock
        variant="display"
        className={className}
        label={`Ad · ${slot}`}
        height={maxH}
      />
    );
  }
  if (!live) return null;
  if (fit === "reject") return null;

  const displayH = fit === "fit" && fittedH != null ? fittedH : maxH;

  return (
    <div className={`my-2 w-full px-6 box-border ${className}`}>
      <div
        ref={rootRef}
        className={`rw-ad-horizontal ${
          fit === "pending" ? "is-pending" : "is-fit"
        }`}
        style={
          {
            ["--rw-ad-h"]: `${displayH}px`,
          } as CSSProperties
        }
      >
        <ins
          key={`${slot}-${attempt}`}
          className="adsbygoogle"
          style={{
            display: "inline-block",
            width: "100%",
          }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slot}
          data-ad-format="horizontal"
          data-rw-attempt={attempt}
        />
      </div>
    </div>
  );
}

export default AdSenseResponsive;
